import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
  Box,
  Link,
  FormMessage,
  Tag,
  Typography,
  TextLink,
} from "@orion/ui";

import { useTranslation } from "react-i18next";

import { LoginFormType } from "../schemas/login";
import { FC, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { Oblique, SecurePassword } from "@orion/icons";
import { SECURITY_URL } from "@delorean/_shared/constants";

type Props = {
  form: UseFormReturn<LoginFormType>;
  onSubmitForm: (values: LoginFormType) => void;
  goToResetPassword: () => void;
  withCaptcha?: boolean;
};

const LoginFormComponent: FC<Props> = ({
  form,
  onSubmitForm,
  goToResetPassword,
}) => {
  const { t } = useTranslation();
  const contractCodeRef = useRef<HTMLInputElement | null>(null);
  const userCodeRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmitForm)}>
        <Box flex gap13 column>
          <Box
            flex
            flexDirection="row"
            gap15
            style={{
              width: "576px",
              minHeight: "44px",
            }}
          >
            {/* Contract Code */}
            <FormField
              control={form.control}
              name="contractCode"
              render={({ field }) => (
                <FormItem style={{ width: "272px" }}>
                  <FormControl>
                    <Input
                      {...field}
                      ref={(el) => {
                        field.ref(el);
                        contractCodeRef.current = el;
                      }}
                      onChange={(e) => {
                        //Rimpiazza ogni carattere non numerico con stringa vuota
                        field.onChange(e.replace(/[^0-9]/g, ""));
                      }}
                      aria-label={t("accessibility.contractCode")}
                      label={t("login.form.contractCode")}
                      placeholder={t("login.form.contractCode")}
                      aria-required="true"
                      aria-invalid={!!form.formState.errors.contractCode}
                      aria-describedby="contractCode-error"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* User Code */}
            <FormField
              control={form.control}
              name="userCode"
              render={({ field }) => (
                <FormItem style={{ width: "272px" }}>
                  <FormControl>
                    <Input
                      {...field}
                      ref={(el) => {
                        field.ref(el);
                        userCodeRef.current = el;
                      }}
                      onChange={(e) => {
                        //Rimpiazza ogni carattere non alfanumerico con stringa vuota
                        field.onChange(e.replace(/[^0-9A-z]/g, ""));
                      }}
                      aria-label={t("accessibility.userCode")}
                      label={t("login.form.userCode")}
                      placeholder={t("login.form.userCode")}
                      aria-required="true"
                      aria-invalid={!!form.formState.errors.userCode}
                      aria-describedby="userCode-error"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </Box>

          {/* Password */}
          <Box
            style={{
              width: "576px",
              minHeight: "44px",
            }}
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      ref={(e) => {
                        field.ref(e);
                        passwordRef.current = e;
                      }}
                      onChange={(e) => {
                        //Rimpiazza ogni spazio con stringa vuota
                        field.onChange(e.replace(/ +/g, ""));
                      }}
                      type="password"
                      aria-label={t("accessibility.password")}
                      label={t("login.form.password")}
                      placeholder={t("login.form.password")}
                      aria-required="true"
                      aria-invalid={!!form.formState.errors.password}
                      aria-describedby="password-error"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </Box>
        </Box>
        {/* Reset PSW */}
        <Box width="fit" my11 /* style={{ visibility: "hidden" }} */>
          <Link
            type="button"
            color="primary100"
            style={{ userSelect: "none", cursor: "pointer" }}
            onClick={() => goToResetPassword()}
            onPress={() => goToResetPassword()}
            role="link"
            aria-label={t("login.forgetPassword")}
          >
            {t("login.forgetPassword")}
          </Link>
        </Box>
        <Box justifyItems="center" mt15>
          <Button type="submit" minW52 textMd>
            {t("login.enter")}
          </Button>
        </Box>

      </form>
      <Tag
        bgNeutral20
        p10
        wFull
        style={{ display: "block", whiteSpace: "normal" }}
      >
        <Box flex gap10 itemsCenter>
          <Box flex column style={{ flexShrink: 0 }}>
            <SecurePassword style={{ width: 24, height: 24 }} />
          </Box>
          <Box flex column style={{ minWidth: 0 }}>
            <Typography textNeutral100>
              {t("login.antiFraud")}
            </Typography>
            <TextLink pt3 href={SECURITY_URL} target="_blank">
              {t("login.antiFraudLink")} <Oblique style={{ width: 12, height: 12 }} />
            </TextLink>
          </Box>
        </Box>
      </Tag>
    </Form>
  );
};

export default LoginFormComponent;
